import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck, Zap, BarChart3, Users, Lock,
  ArrowRight, HelpCircle, Loader2,
} from "lucide-react";
import { useAuth } from "@/App";

/**
 * Login · pixel-perfect da referência HERMES (Fev/2026).
 * Layout desktop: 63% arte à esquerda · 37% painel navy à direita.
 * Autenticação REAL preservada: Emergent Google Auth → /dashboard.
 */

const GOLD = "#D9A348";
const GOLD_LIGHT = "#E7B65D";
const OFF_WHITE = "#F4F0E7";
const MUTED = "#A8ADB3";
const NAVY = "#03111B";
const NAVY_DEEP = "#020A12";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [googleBusy, setGoogleBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (user && !loading) navigate("/dashboard");
  }, [user, loading, navigate]);

  const startGoogle = () => {
    if (googleBusy) return;
    setGoogleBusy(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden flex"
      data-testid="login-page"
      style={{ background: NAVY_DEEP, color: OFF_WHITE, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Imagem hero única cobrindo TODO o viewport. Sem duplicação —
          o painel de login será uma sobreposição translúcida sobre esta mesma
          imagem, evitando qualquer artefato de duplicação. */}
      <img
        src="/hermes-hero.jpg"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        style={{ objectPosition: "center center", zIndex: 0 }}
      />
      {/* Vinheta inferior para dar peso à frase serifada */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none hidden md:block"
        style={{
          background: "linear-gradient(to bottom, rgba(2,8,14,0) 55%, rgba(2,8,14,0.55) 100%)",
          zIndex: 1,
        }}
      />

      {/* =============== LEFT · HERO (63%) =============== */}
      <section
        className="relative hidden md:block"
        data-testid="login-hero"
        style={{ flex: "0 0 63%", zIndex: 2 }}
      >
        {/* Frase inferior esquerda — mesma família de fonte do slogan
            "ADVOCACIA INTELIGENTE" (IBM Plex Sans, herdada do body) */}
        <div
          className="absolute"
          style={{ left: "clamp(32px, 4.2vw, 72px)", bottom: "clamp(24px, 3.5vh, 44px)" }}
          data-testid="login-hero-quote"
        >
          <h2
            className="font-semibold"
            style={{
              fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
              color: OFF_WHITE,
              fontSize: "clamp(30px, 3.1vw, 46px)",
              lineHeight: 1.02,
              letterSpacing: "-0.005em",
              textShadow: "0 2px 20px rgba(0,0,0,0.55)",
            }}
          >
            Onde o tempo<br />
            do advogado vira<br />
            <span style={{ color: GOLD }}>resultado.</span>
          </h2>
          <div
            className="mt-6"
            style={{
              fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
              fontSize: "11.5px",
              letterSpacing: "0.42em",
              color: "rgba(244,240,231,0.72)",
              lineHeight: 1.75,
            }}
          >
            TECNOLOGIA JURÍDICA<br />
            QUE IMPULSIONA O SEU TEMPO.
          </div>
          <div
            className="mt-4"
            style={{ width: 52, height: 2, background: GOLD, borderRadius: 1 }}
            aria-hidden="true"
          />
        </div>
      </section>

      {/* =============== RIGHT · LOGIN (37%) =============== */}
      <section
        className="relative flex-1 flex flex-col overflow-hidden"
        data-testid="login-panel"
        style={{
          background: "rgba(3, 17, 27, 0.30)",
          backdropFilter: "blur(6px) saturate(1.05)",
          WebkitBackdropFilter: "blur(6px) saturate(1.05)",
          minWidth: 0,
          zIndex: 2,
        }}
      >
        {/* Wrapper de conteúdo */}
        <div className="relative flex-1 flex flex-col" style={{ zIndex: 1 }}>
        {/* Slogan superior direito */}
        <div
          className="absolute text-center"
          style={{
            top: "clamp(20px, 3vh, 32px)",
            right: "clamp(48px, 7vw, 110px)",
            color: GOLD,
            fontSize: "10.5px",
            letterSpacing: "0.38em",
            lineHeight: 1.7,
          }}
          data-testid="login-slogan"
        >
          ADVOCACIA INTELIGENTE<br />
          RESULTADOS REAIS
          <div
            className="mx-auto mt-2.5"
            style={{ width: 46, height: 1.5, background: GOLD, borderRadius: 1 }}
            aria-hidden="true"
          />
        </div>

        {/* Container centralizado */}
        <div
          className="flex-1 flex flex-col items-center justify-center px-8"
          style={{ paddingTop: "clamp(80px, 9vh, 110px)", paddingBottom: "clamp(20px, 2vh, 32px)" }}
        >
          <div className="w-full" style={{ maxWidth: 440 }}>
            {/* Logotipo HERMES (wing + wordmark + tagline) */}
            <div className="flex items-center justify-center gap-4 mb-9" data-testid="login-brand">
              <img
                src="/hermes-wing.png"
                alt="Hermes"
                draggable={false}
                style={{
                  height: 87,
                  width: "auto",
                  filter: "drop-shadow(0 6px 18px rgba(217,163,72,0.28))",
                }}
              />
              <div className="flex flex-col leading-none">
                <span
                  className="font-serif"
                  style={{
                    color: GOLD,
                    fontSize: 54,
                    fontWeight: 600,
                    letterSpacing: "0.005em",
                    lineHeight: 1,
                  }}
                >
                  HERMES
                </span>
                <span
                  className="mt-2"
                  style={{
                    color: "rgba(217,163,72,0.88)",
                    fontSize: 10,
                    letterSpacing: "0.32em",
                    lineHeight: 1.6,
                  }}
                >
                  TECNOLOGIA JURÍDICA<br />
                  QUE IMPULSIONA RESULTADOS
                </span>
              </div>
            </div>

            {/* Título */}
            <h1
              className="font-serif text-center"
              style={{
                color: OFF_WHITE,
                fontSize: 30,
                fontWeight: 600,
                letterSpacing: "-0.005em",
                lineHeight: 1.15,
              }}
              data-testid="login-title"
            >
              Bem-vindo ao Hermes
            </h1>
            <p
              className="text-center mt-2"
              style={{ color: MUTED, fontSize: 16.5, lineHeight: 1.4 }}
              data-testid="login-subtitle"
            >
              Acesse sua plataforma jurídica.
            </p>

            {/* Botão Google */}
            <button
              type="button"
              onClick={startGoogle}
              disabled={googleBusy}
              data-testid="btn-entrar"
              className="group relative w-full inline-flex items-center transition-all"
              style={{
                marginTop: 28,
                height: 60,
                padding: "0 22px",
                borderRadius: 12,
                background: OFF_WHITE,
                color: "#0f1218",
                fontSize: 15.5,
                fontWeight: 500,
                cursor: googleBusy ? "wait" : "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#FFFFFF")}
              onMouseLeave={(e) => (e.currentTarget.style.background = OFF_WHITE)}
            >
              <GoogleGlyph />
              <span className="flex-1 text-center" style={{ letterSpacing: "0.01em" }}>
                {googleBusy ? "Conectando..." : "Entrar com Google"}
              </span>
              {googleBusy
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : <ArrowRight className="w-5 h-5 opacity-80 transition-transform group-hover:translate-x-0.5" />}
            </button>

            {/* Botão Criar conta */}
            <button
              type="button"
              onClick={() => navigate("/criar-conta")}
              data-testid="btn-criar-conta"
              className="w-full inline-flex items-center justify-center transition-all"
              style={{
                marginTop: 12,
                height: 60,
                borderRadius: 12,
                background: "transparent",
                border: `1px solid ${GOLD}`,
                color: GOLD,
                fontSize: 15.5,
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(217,163,72,0.09)";
                e.currentTarget.style.color = GOLD_LIGHT;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = GOLD;
              }}
            >
              Criar conta
            </button>

            {/* Precisa de ajuda? */}
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              data-testid="login-help"
              className="mx-auto mt-5 inline-flex items-center gap-2 transition-colors"
              style={{ color: MUTED, fontSize: 13.5 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = OFF_WHITE)}
              onMouseLeave={(e) => (e.currentTarget.style.color = MUTED)}
            >
              <HelpCircle className="w-4 h-4" />
              <span>Precisa de ajuda?</span>
            </button>

            {/* Benefícios */}
            <div
              className="grid grid-cols-4 mt-10"
              style={{ gap: 16 }}
              data-testid="login-benefits"
            >
              <Benefit icon={ShieldCheck} l1="Dados" l2="protegidos" testid="benefit-dados" />
              <Benefit icon={Zap}         l1="Mais tempo" l2="para o que importa" testid="benefit-tempo" />
              <Benefit icon={BarChart3}   l1="Produtividade" l2="no seu dia a dia" testid="benefit-produtividade" />
              <Benefit icon={Users}       l1="Ferramentas" l2="em um só lugar" testid="benefit-ferramentas" />
            </div>

            {/* Linha dourada */}
            <div
              className="mx-auto mt-9"
              style={{
                width: "100%",
                height: 1,
                background: `linear-gradient(90deg, transparent 0%, ${GOLD}55 20%, ${GOLD}55 80%, transparent 100%)`,
              }}
              aria-hidden="true"
            />

            {/* Segurança inferior */}
            <div
              className="flex items-center justify-center gap-2 mt-5"
              data-testid="login-security"
              style={{ color: MUTED, fontSize: 12.5 }}
            >
              <Lock className="w-3.5 h-3.5" style={{ color: GOLD }} />
              <span>
                Acesso seguro por <span style={{ color: OFF_WHITE }}>Google</span> · dados criptografados
              </span>
            </div>
          </div>
        </div>
        </div>
      </section>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

/* ---------- Componentes locais ---------- */

function Benefit({ icon: Icon, l1, l2, testid }) {
  return (
    <div className="flex flex-col items-center text-center" data-testid={testid}>
      <Icon
        className="mb-2"
        style={{ width: 24, height: 24, color: GOLD, strokeWidth: 1.5 }}
      />
      <div style={{ color: OFF_WHITE, fontSize: 12, lineHeight: 1.35, fontWeight: 500 }}>{l1}</div>
      <div style={{ color: MUTED, fontSize: 11.5, lineHeight: 1.35 }}>{l2}</div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function HelpModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center p-6"
      style={{ background: "rgba(2,8,14,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="support-modal"
    >
      <div
        className="max-w-md w-full rounded-2xl p-6 shadow-2xl"
        style={{
          background: `linear-gradient(140deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)`,
          border: `1px solid ${GOLD}44`,
          color: OFF_WHITE,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-3">
          <img src="/hermes-wing.png" alt="" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-serif text-lg" style={{ color: GOLD_LIGHT }}>Suporte HERMES</div>
            <div className="text-xs" style={{ color: MUTED }}>Tire suas dúvidas em qualquer canal</div>
          </div>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: "rgba(244,240,231,0.85)" }}>
          Se preferir falar direto com o escritório, escolha um dos canais:
        </p>
        <div className="mt-4 space-y-2">
          <a
            href="mailto:delpadrestudio@gmail.com?subject=Suporte%20Hermes"
            data-testid="help-mail"
            className="block w-full h-11 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 hover:brightness-105 transition"
            style={{ background: GOLD, color: "#0f1218" }}
          >
            Enviar e-mail
          </a>
          <a
            href="https://wa.me/5551999999999?text=Preciso%20de%20ajuda%20com%20o%20Hermes"
            target="_blank" rel="noreferrer" data-testid="help-wa"
            className="block w-full h-11 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 transition"
            style={{ background: "rgba(2,8,14,0.5)", border: `1px solid ${GOLD}66`, color: GOLD_LIGHT }}
          >
            Falar no WhatsApp
          </a>
        </div>
        <button
          onClick={onClose}
          data-testid="help-close"
          className="mt-4 w-full text-xs transition-colors"
          style={{ color: MUTED }}
          onMouseEnter={(e) => (e.currentTarget.style.color = OFF_WHITE)}
          onMouseLeave={(e) => (e.currentTarget.style.color = MUTED)}
        >
          Fechar
        </button>
      </div>
    </div>
  );
}
